import type { PlanNodeOut } from '@/client';
import { canSendPlanAction } from '@/components/maintenance/model/planActions';
import { isReviewable } from '@/components/maintenance/model/planReviewFilter';
import type { DismissReason, PlanPlacement } from '@/components/maintenance/model/planVocabulary';
import { PlanActions } from '@/components/maintenance/model/planVocabulary';

/**
 * Which way a proposal went.
 *
 * `Kept` is named for `review.status`, which is what the next read will say. `Dismissed` is not —
 * the read spells that one `removed` — so neither can be passed to the endpoint or compared against
 * a card without going through the mapping that knows the difference.
 */
export const ReviewVerdicts = {
	Kept: 'kept',
	Dismissed: 'dismissed',
} as const;

export type ReviewVerdict = (typeof ReviewVerdicts)[keyof typeof ReviewVerdicts];

/**
 * The optional why on a dismissal, given alongside the verdict rather than folded into it.
 *
 * Separate from `ReviewVerdict` because the queue tracks only which way a card went: pending,
 * settled or refused is the same question whether or not a reason came with it.
 *
 * The request is a different matter. The reason and the note are sent with the dismissal, so
 * changing either makes a different request and it has to go under a different key — the endpoint
 * answers 409 for a key reused with a changed body. **Every field added here belongs in
 * `verdictDetailKey`**; one left out lets two different requests share a token, which is the
 * collision this module exists to prevent.
 */
export interface DismissDetail {
	reason: DismissReason;
	note?: string;
}

/**
 * Everything that can travel alongside a verdict, whichever way the card went.
 *
 * One shape rather than a union because the queue only ever passes it through: it holds a detail
 * against a card, keys a token on it, and hands it back to whoever builds the request. Which fields
 * are meaningful is the verdict's business, and a `DismissDetail` is already one of these.
 *
 * `placement` is the surface a kept card was elected onto. Absent means the household did not
 * choose and the server resolves its own default.
 */
export interface VerdictDetail {
	reason?: DismissReason;
	note?: string;
	placement?: PlanPlacement;
	/**
	 * A corrected cadence, sent on the keep rather than as a second write.
	 *
	 * The endpoint applies both in one transaction, so a half-failure cannot leave a card
	 * re-cadenced but still awaiting its verdict.
	 */
	cadence?: string;
	/** A corrected first-due date, sent on the same keep as the cadence. */
	anchorDate?: string;
}

/**
 * How far a verdict has got.
 *
 * The distinction this exists to enforce: a verdict is a request, not a fact, until the server says
 * so. Only `succeeded` removes a card and advances the count. An earlier version of this module
 * settled locally and sent afterwards, which meant a refused write left the card gone, the progress
 * advanced, and — on a queue whose whole purpose is measuring generation quality — a verdict
 * recorded against a card the server never heard about.
 */
export type VerdictPhase = 'pending' | 'succeeded' | 'failed';

export interface VerdictRecord {
	verdict: ReviewVerdict;
	phase: VerdictPhase;
}

/** A key and the request it belongs to. The pairing is the point — see `tokenForVerdict`. */
export interface HeldToken {
	verdict: ReviewVerdict;
	/** What travelled with the verdict is part of the request, and therefore part of its identity. */
	detailKey: string;
	clientToken: string;
}

/**
 * Stable identity for the fields that make one verdict request differ from another.
 *
 * Positional rather than over the object, because `JSON.stringify` follows insertion order — keying
 * on `{ reason, note }` would give one request two identities depending on how it was built. The
 * cost of the array is that it is hand-listed: a field added to `VerdictDetail` belongs in it.
 *
 * **A keep is keyed on its placement, its cadence and its date**, which is what makes promoting, demoting and
 * correcting work. Each is the same verb on the same card and differs only in those values, so a
 * shared key would have the endpoint replay the first response for the second — the card would
 * appear to change and nothing would be written.
 */
const verdictDetailKey = (verdict: ReviewVerdict, detail?: VerdictDetail): string =>
	verdict === ReviewVerdicts.Dismissed
		? JSON.stringify([detail?.reason ?? null, detail?.note ?? null])
		: JSON.stringify([detail?.placement ?? null, detail?.cadence ?? null, detail?.anchorDate ?? null]);

/**
 * The idempotency key to send this verdict under, given whatever key the card is already holding.
 *
 * The endpoint replays the stored response for a repeat of the same key and answers 409 for a key
 * reused with a *different* request. Both halves matter here, and an earlier version only had one
 * of them: it keyed on the card, so pressing Dismiss after a refused Keep sent a different request
 * under the Keep's key and the card could never be settled at all.
 *
 * So a key belongs to the complete request rather than to a card. The same verdict with the same
 * dismissal feedback is a retry and keeps its key; changing the verdict, reason, or note is a new
 * request and gets a new one. A retried write that had in fact landed is replayed rather than
 * repeated, while edited feedback cannot collide with the request sent before it.
 */
export const tokenForVerdict = (
	held: HeldToken | undefined,
	verdict: ReviewVerdict,
	detail?: VerdictDetail,
): HeldToken => {
	const detailKey = verdictDetailKey(verdict, detail);
	return held?.verdict === verdict && held.detailKey === detailKey
		? held
		: { verdict, detailKey, clientToken: newClientToken() };
};

export interface ReviewQueueState {
	verdicts: ReadonlyMap<string, VerdictRecord>;
}

export const emptyReviewQueue: ReviewQueueState = { verdicts: new Map() };

export const newClientToken = (): string => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

/**
 * Parent context for detached review rows.
 *
 * Groups now remain intact because the endpoint defines each as one review and paging item. The
 * review components still accept this map, so return its honest empty state rather than maintaining
 * a second tree walk for a mode the contract no longer supports.
 */
/** The endpoint pages and reviews a group as one item, so its children never become extra verdicts. */
export const proposalsIn = (nodes: readonly PlanNodeOut[]): PlanNodeOut[] => nodes.filter(isReviewable);

/**
 * The verb that rejects this card, which is not the same verb for a group as for a task.
 *
 * Group parents advertise `keep` and `bulk_dismiss` and never plain `dismiss`; leaves advertise
 * `keep` and `dismiss` and never the bulk form. So keeping is symmetric and rejecting is not: a
 * group is turned down by dismissing what it holds. There is no `bulk_keep` anywhere in the action
 * union, which is the same asymmetry read from the other side.
 *
 * `null` means the card advertises no way to be turned down, and the caller withholds the control
 * rather than offering one that cannot be sent.
 */
export const dismissActionFor = (
	node: PlanNodeOut,
): typeof PlanActions.Dismiss | typeof PlanActions.BulkDismiss | null => {
	if (canSendPlanAction(node, PlanActions.Dismiss)) return PlanActions.Dismiss;
	if (canSendPlanAction(node, PlanActions.BulkDismiss)) return PlanActions.BulkDismiss;
	return null;
};

/** Marks a verdict as sent and awaiting its answer. */
export const beginVerdict = (state: ReviewQueueState, nodeId: string, verdict: ReviewVerdict): ReviewQueueState => {
	const verdicts = new Map(state.verdicts);
	verdicts.set(nodeId, { verdict, phase: 'pending' });
	return { verdicts };
};

/**
 * Records what the server said.
 *
 * A refusal leaves the card on screen carrying its verdict, so pressing the same button again
 * retries that request rather than writing a second one.
 */
export const finishVerdict = (state: ReviewQueueState, nodeId: string, didLand: boolean): ReviewQueueState => {
	const existing = state.verdicts.get(nodeId);
	if (!existing) return state;

	const verdicts = new Map(state.verdicts);
	verdicts.set(nodeId, { ...existing, phase: didLand ? 'succeeded' : 'failed' });
	return { verdicts };
};

export interface ReviewProgress {
	/** Still on screen: undecided, in flight, or refused and waiting to be sent again. */
	remaining: PlanNodeOut[];
	/** Verdicts the server has accepted. The only number that may be called progress. */
	settled: number;
	/** Refused writes still on screen. Surfaced so the pass can say so rather than quietly stalling. */
	failed: number;
	total: number;
}

/** One pass rather than two, this being read on every render of a list the size of a whole plan. */
export const reviewProgress = (proposals: readonly PlanNodeOut[], state: ReviewQueueState): ReviewProgress => {
	const remaining: PlanNodeOut[] = [];
	let failed = 0;

	for (const node of proposals) {
		const phase = state.verdicts.get(node.node_id)?.phase;
		if (phase === 'succeeded') continue;

		remaining.push(node);
		if (phase === 'failed') failed += 1;
	}

	return { remaining, settled: proposals.length - remaining.length, failed, total: proposals.length };
};
